<template>
  <div class="sharing-view">
    <div>
      <div class="sharing-view-content">
        <div class="iframe-placement">
          <iframe
            id="shared-presentation"
            frameborder="0"
            :src="sharingViewUrl"
            ref="sharingViewIframe"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
            allowfullscreen
            @load="onSharingViewLoad"
          />
        </div>
      </div>
      <div class="sharing-view-actions navigator py-1">
        <span
          class="action"
          :class="{
            disabled: isPrevSlideDisabled
          }"
          v-aha-tooltip="{arrow: true}"
          :content="$i18nPresenter.t('previous')"
          @click="onClickPreviousSlide"
        >
          <aha-icon
            class="prev"
            name="arrow"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          />
        </span>
        <span class="number">{{ currentSharingSlideIndex + 1 }}/{{ maxLookbackSlideIndex + 1 }}</span>
        <span
          class="action"
          :class="{
            disabled: isNextSlideDisabled
          }"
          v-aha-tooltip="{arrow: true}"
          :content="$i18nPresenter.t('next')"
          @click="onClickNextSlide"
        >
          <aha-icon
            class="next"
            name="arrow"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { sortBy } from 'lodash'
import Util from '@/util'
import Config from '@/config'

const EVENT_CHANGE_SLIDE = 'template-preview:change-slide'

export default {
  props: {
    presentation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentSharingSlideIndex: 0,
      isSharingViewLoaded: false,
    }
  },
  computed: {
    slides() {
      if (!this.presentation) return []
      return sortBy(this.presentation.slides, 'order')
    },
    maxLookbackSlideIndex() {
      return this.presentation.slides.length - 1
    },
    isPrevSlideDisabled() {
      return !this.isSharingViewLoaded || this.currentSharingSlideIndex === 0
    },
    isNextSlideDisabled() {
      if (!this.isSharingViewLoaded) return true

      return this.currentSharingSlideIndex >= this.maxLookbackSlideIndex
    },
    sharingViewUrl() {
      const {
        slugifiedName: slug, shareCode, planType, user,
      } = this.presentation
      const whiteLabelPresenterDomain = user && user.whiteLabelPresenterDomain
      const params = {
        tracking: false,
        hideText: true,
        hideAccessCode: true,
        hideSlideNavigation: true,
        hideBackGround: true,
        swipeControl: true,
      }

      const paramString = Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join('&')

      const { presenterDomain } = Config
      let baseUrl = presenterDomain
      let path = `/share/${shareCode}`

      if (
        Util.checkWhiteLabelDomain()
          && whiteLabelPresenterDomain
          && planType === 'white-label'
      ) {
        baseUrl = `https://${whiteLabelPresenterDomain}`
      } else if (slug && shareCode && shareCode.indexOf('-') > -1) {
        path = `/share/${slug}-${shareCode}`
      }

      return `${baseUrl}${path}?${paramString}`
    },
    isShowEmptyScreen() {
      return this.maxLookbackSlideIndex === -1
    },
  },
  mounted() {
    window.addEventListener('message', (event) => {
      if (event.data.direction === 'left') {
        this.changeSlide(this.currentSharingSlideIndex + 1, true)
      } else if (event.data.direction === 'right') {
        this.changeSlide(this.currentSharingSlideIndex - 1)
      }
    }, false)
  },
  methods: {
    onClickPreviousSlide() {
      this.changeSlide(this.currentSharingSlideIndex - 1)
    },
    onClickNextSlide() {
      this.changeSlide(this.currentSharingSlideIndex + 1, true)
    },
    changeSlide(targetSlideIndex, isNextSlide = false) {
      if (targetSlideIndex < 0) return

      if (isNextSlide && this.isNextSlideDisabled) return

      this.currentSharingSlideIndex = targetSlideIndex

      this.$nextTick(() => {
        this._postMessage(this.slides[this.currentSharingSlideIndex])
      })
    },
    _postMessage(slide) {
      if (!slide || !slide.id) return

      const iframe = document.getElementById('shared-presentation')
      const iframeContentWindow = iframe && iframe.contentWindow

      if (!iframeContentWindow) return

      iframeContentWindow.postMessage({
        type: EVENT_CHANGE_SLIDE,
        slide,
      }, '*')
    },
    onSharingViewLoad() {
      setTimeout(() => {
        this.isSharingViewLoaded = true

        this._postMessage(this.slides[this.currentSharingSlideIndex])
      }, 3000)
    },
  },
}
</script>

  <style lang="scss" scoped>
  .sharing-view {
    iframe {
      border: none !important;
    }
  }

  .sharing-view-content {
    background-color: #fff;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom: 1px solid #eee;
  }

  .iframe-placement {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    max-width: 100%;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  .navigator {
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;

    .action {
      display: flex;
      cursor: pointer;
      padding: 2px;
      color: #0A0A0A;

      &:hover {
        background-color: #E3E5EB;
        border-radius: 50%;
      }

      &.disabled {
        cursor: not-allowed;
        color: #B5B5B5;
      }

      .prev {
        transform: rotate(90deg) translateY(2px);
      }

      .next {
        transform: rotate(-90deg) translateY(2px);
      }
    }

    .number {
      font-size: 14px;
      font-weight: 700;
      line-height: 0.5; // life-hack to vertical center the number
      color: #191F2D;
      width: max-content;
      text-align: center;
      margin-left: 12px;
      margin-right: 12px;
    }
  }
  </style>
